/**
 * useRecommendCards · 首页推荐卡的取批与「换一批」轮换
 *
 * 数据源: sessionSlice.recommendCards（v2 init 预拼的「单/单/单/多」完整序列，
 * 由 loadRecommendCards 在进入页面 / 切团队时写入）。本 hook 只做视图层切批：
 * 每批固定 4 张（滑动窗口 + 回卷），refreshGroup 向后翻一批、翻完循环回第 1 批。
 *
 * 批下标是组件局部状态：EmptyState（建夹缺省页）与 ChatFeed（助手空会话）各自
 * 独立轮换互不干扰；卡片数据本体在 redux（随 teamSwitched 清空、v2 init 重新填回）。
 * canRefresh 供调用方决定是否渲染「换一批」按钮（≤4 张没有下一批即隐藏）。
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppSelector } from '../../../../app/hooks'
import {
  selectRecommendCards,
  selectRecommendCardsLoading
} from '../../../../slices/aiClassify'
import {
  canRotateRecommendCards,
  pickRecommendCardGroup,
  recommendCardCycleCount
} from '../../utils/recommendCards'
import type { RecommendCardVM } from './types'

export interface UseRecommendCardsResult {
  /** 当前批的卡片（固定 4 张；≤4 张时为全部；数据空 / 拉取失败时为 FALLBACK 兜底） */
  cards: RecommendCardVM[]
  /** v2 init 是否还在飞行中 · true 时调用方先不渲染推荐卡，避免兜底闪现 */
  loading: boolean
  /** 是否可「换一批」· false 时调用方隐藏「换一批」按钮（卡片数 ≤4，没有下一批） */
  canRefresh: boolean
  /** 「换一批」· 向后翻一批（滑动窗口步长 4），翻完循环回第 1 批 */
  refreshGroup: () => void
}

/**
 * 管理首页推荐卡的当前批与「换一批」轮换。
 * 由 EmptyState / ChatFeed 空态调用，内部消费 sessionSlice 的 v2 init 数据。
 *
 * @returns 当前批卡片、加载态、是否可换一批与翻批操作，见 UseRecommendCardsResult
 */
export function useRecommendCards(): UseRecommendCardsResult {
  const allCards = useAppSelector(selectRecommendCards)
  const loading = useAppSelector(selectRecommendCardsLoading)
  const [batchIndex, setBatchIndex] = useState(0)

  // 数据整体替换（切团队重拉）后从第 1 批看起；pick 内部另有取模回卷守卫
  useEffect(() => {
    setBatchIndex(0)
  }, [allCards])

  const cards = useMemo(
    () => pickRecommendCardGroup(allCards, batchIndex),
    [allCards, batchIndex]
  )

  const canRefresh = canRotateRecommendCards(allCards)

  const refreshGroup = useCallback(() => {
    setBatchIndex(
      (current) => (current + 1) % recommendCardCycleCount(allCards)
    )
  }, [allCards])

  return { cards, loading, canRefresh, refreshGroup }
}
